import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { PaymentTransactionsExportColumn } from "../../types/paymentTransactions";
import { getPrivateInstance } from "../_privateInstance";

export const PAYMENT_TRANSACTIONS_EXPORT_COLUMNS_KEY =
  "PAYMENT_TRANSACTIONS_EXPORT_COLUMNS";

type ColumnSource =
  | string
  | {
      key?: string;
      value?: string;
      name?: string;
      label?: string;
      display_name?: string;
      always_included?: boolean;
    };

const normalizeColumns = (
  source: unknown,
): PaymentTransactionsExportColumn[] => {
  if (!Array.isArray(source)) return [];
  return source.flatMap((column: ColumnSource) => {
    if (typeof column === "string") {
      return [{ key: column, label: column }];
    }
    const key = column?.key ?? column?.value ?? column?.name;
    if (!key) return [];
    return [
      {
        key,
        label: column.label ?? column.display_name ?? key,
        always_included: column.always_included === true,
      },
    ];
  });
};

export function extractPaymentTransactionExportColumns(
  body: unknown,
): PaymentTransactionsExportColumn[] {
  const response = body as {
    data?: {
      columns?: unknown;
      meta?: { filters?: { columns?: unknown } };
    };
    meta?: {
      filters?:
        | { columns?: unknown }
        | Array<{ key?: string; options?: unknown }>;
    };
  } | null;
  const filters = response?.meta?.filters;
  const descriptorColumns = Array.isArray(filters)
    ? filters.find((filter) => filter?.key === "columns")?.options
    : undefined;
  const source =
    response?.data?.columns ??
    (!Array.isArray(filters) ? filters?.columns : undefined) ??
    response?.data?.meta?.filters?.columns ??
    descriptorColumns;
  return normalizeColumns(source);
}

export async function fetchPaymentTransactionExportColumns(
  signal?: AbortSignal,
): Promise<PaymentTransactionsExportColumn[]> {
  const response = await getPrivateInstance().get(
    API_ROUTES.PAYMENT_TRANSACTIONS_EXPORT_COLUMNS,
    { signal },
  );
  return extractPaymentTransactionExportColumns(response?.data);
}

export function usePaymentTransactionsExportColumnCatalog({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) {
  const query = useQuery({
    queryKey: [PAYMENT_TRANSACTIONS_EXPORT_COLUMNS_KEY],
    enabled,
    staleTime: 30 * 60 * 1000,
    queryFn: ({ signal }) =>
      fetchPaymentTransactionExportColumns(signal),
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export default usePaymentTransactionsExportColumnCatalog;
